<template>
	<div
		ref="viewportEl"
		class="zoomable-photo"
		:style="{ cursor: zoom.cursor.value, touchAction: zoom.zoomed.value ? 'none' : 'auto' }"
		@pointerdown="zoom.onPanStart"
		@dblclick="zoom.stepOnDoubleClick"
		@touchstart="stopTouchWhileZoomed"
		@touchmove="stopTouchWhileZoomed"
		@touchend="stopTouchWhileZoomed">
		<div class="zoomable-photo__wrapper" data-test-id="lightbox-zoom-wrapper" :style="{ transform: zoom.transform.value }">
			<!-- No fixed max-height cap and no aspect ratio on desktop (LSP-12175, LSP-17655): the
			     image must fill the media frame's full height, not top out at 800px or at a
			     width-derived box. `max-height: 100%` keeps it bounded to the wrapper.
			     LSP-19030 restored the cap for small screens only. The frame's mobile HEIGHT is not decided
			     here: since LSP-21116 MediaLightBox gives the parent frame a fixed px height and this image
			     fills it, and it passes no aspect ratio. The breakpoint decision stays in MediaLightBox. -->
			<v-img :src="src" :lazy-src="lazySrc" :aspect-ratio="effectiveAspectRatio" :style="imageStyle">
				<template #placeholder>
					<div class="d-flex align-center justify-center fill-height">
						<v-progress-circular color="grey-lighten-4" indeterminate></v-progress-circular>
					</div>
				</template>
				<!-- Invisible img overlay preserves right-click "copy image" over the v-img background. -->
				<img
					:src="src"
					:alt="alt || 'Media image'"
					draggable="false"
					style="
						position: absolute;
						top: 0;
						left: 0;
						width: 100%;
						height: 100%;
						opacity: 0;
						object-fit: contain;
						user-select: none;
					"
					@load="onImageLoad"
					@contextmenu.stop />
				<!-- Transforms with the photo (zoom/pan), sized to the v-img box — e.g. the measure outline. -->
				<slot name="image-overlay" :natural-width="naturalWidth" :natural-height="naturalHeight" />
			</v-img>
		</div>

		<!--
			LSP-20000 (epic LSP-16731): Zoom out at 100% and Zoom in at 400% stay clickable rather than
			going native-disabled at their bounds. The range clamp is unchanged and still lives in
			useImageZoom's setLevel(), which pins the requested level into [0, steps.length - 1] — so a
			click at either bound is a safe no-op and no invalid zoom level is reachable.
			LSP-21222: at its bound a control still reads as unavailable — dimmed, aria-disabled, and a
			tooltip saying why — so a click there is no longer a silent, broken-looking no-op.
		-->
		<div class="zoomable-photo__pill" @pointerdown.stop @dblclick.stop>
			<v-btn
				icon
				variant="text"
				size="small"
				class="zoomable-photo__btn"
				:class="{ 'zoomable-photo__btn--at-bound': atMinimumZoom }"
				data-test-id="lightbox-zoom-out"
				aria-label="Zoom out"
				:aria-disabled="atMinimumZoom || undefined"
				@click="zoom.zoomOut">
				<i class="icon-dash"></i>
				<v-tooltip
					activator="parent"
					location="top"
					:disabled="!atMinimumZoom || tooltipsSuppressed"
					text="You're already zoomed all the way out" />
			</v-btn>
			<v-btn
				variant="text"
				size="small"
				class="zoomable-photo__label"
				data-test-id="lightbox-zoom-reset"
				aria-label="Reset zoom"
				@click="zoom.reset">
				{{ zoom.zoomLabel.value }}
			</v-btn>
			<v-btn
				icon
				variant="text"
				size="small"
				class="zoomable-photo__btn"
				:class="{ 'zoomable-photo__btn--at-bound': atMaximumZoom }"
				data-test-id="lightbox-zoom-in"
				aria-label="Zoom in"
				:aria-disabled="atMaximumZoom || undefined"
				@click="zoom.zoomIn">
				<i class="icon-plus"></i>
				<v-tooltip
					activator="parent"
					location="top"
					:disabled="!atMaximumZoom || tooltipsSuppressed"
					text="You're already zoomed all the way in" />
			</v-btn>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { computed, onMounted, onUnmounted, ref, watch } from "vue";

	import { useImageZoom } from "./useImageZoom";

	const props = defineProps<{
		src: string;
		lazySrc?: string;
		alt?: string;
		/**
		 * LSP-19030: optional CSS length capping the rendered image's height, supplied by the parent
		 * lightbox for small screens only. `null`/omitted means "no cap" — the desktop behaviour
		 * LSP-12175 and LSP-17655 introduced, where the image fills the media frame's full height.
		 */
		maxHeight?: string | null;
		/**
		 * LSP-19030: optional aspect ratio for the rendered image. `null`/omitted means "no aspect
		 * constraint" — the image fills its frame and letterboxes against its own natural aspect. The media
		 * lightbox no longer passes one (LSP-21116): its mobile frame is a fixed height, not a 16:9 box.
		 */
		aspectRatio?: number | string | null;
	}>();

	const zoom = useImageZoom();
	const atMinimumZoom = computed(() => zoom.level.value <= 0);
	const atMaximumZoom = computed(() => zoom.level.value >= zoom.steps.length - 1);
	// An explicit :disabled overrides the app-wide VTooltip default (plugins/vuetify.js disables tooltips on touch
	// devices), so honour that suppression here; the dimmed/aria-disabled state still marks the bound on touch.
	const tooltipsSuppressed = typeof window !== "undefined" && window.matchMedia("(hover: none), (pointer: coarse)").matches;
	const viewportEl = ref<HTMLElement | null>(null);
	const naturalWidth = ref(0);
	const naturalHeight = ref(0);

	// break-it round 2: fall back on an empty or whitespace-only cap rather than passing it through.
	// A blank cap is not a cap — rendering it would leave the image with NO max-height at all, the
	// exact regression this component exists to prevent. Every real CSS length (including "0px")
	// survives.
	//
	// break-it round 3: coerce before trimming, so a caller that passes a bare number (Vuetify's own
	// `max-height` accepts one) degrades to an ignored value instead of throwing in render. A height
	// cap is a display detail; it must never be able to blank the whole media frame.
	const effectiveMaxHeight = computed(() => {
		const requestedCap = props.maxHeight == null ? "" : String(props.maxHeight).trim();
		return requestedCap || "100%";
	});

	// LSP-19030: normalised before it reaches VImg, for two reasons — neither of them the one the
	// first draft of this comment claimed. break-it round 6 checked Vuetify 3.12.6 directly: VImg does
	// `Number(props.aspectRatio || 0)` and VResponsive does `ratio ? {...} : undefined`, so
	// `undefined`, `null`, `0`, `NaN` and unparseable strings ALL already fall back to the image's
	// natural aspect. Nothing divides by zero and nothing blanks. So:
	//
	// 1. A NEGATIVE ratio does reach the division and yields `padding-bottom: -100%`, which the
	//    browser drops — leaving the frame silently unconstrained on the very breakpoint that needs
	//    the constraint. That one is worth refusing outright.
	// 2. A CSS-shaped `"16 / 9"` string is the spelling the sibling video branch uses, and a bare
	//    `Number.parseFloat` reads it as **16** — a 16:1 strip, not a 16:9 frame. Parsing the `a / b`
	//    form (and refusing anything else) is what makes the documented contract true rather than
	//    quietly wrong (break-it round 6).
	//
	// The fail-safe posture matches the blank-cap fallback above: a height constraint must never be
	// able to destroy, or silently misshape, the media it constrains.
	const effectiveAspectRatio = computed(() => {
		const requested = props.aspectRatio;
		if (typeof requested === "number") {
			return Number.isFinite(requested) && requested > 0 ? requested : undefined;
		}
		if (typeof requested !== "string") {
			return undefined;
		}
		const cssRatio = requested.trim().match(/^(\d+(?:\.\d+)?)(?:\s*\/\s*(\d+(?:\.\d+)?))?$/);
		if (!cssRatio) {
			return undefined;
		}
		const ratio = Number.parseFloat(cssRatio[1]) / Number.parseFloat(cssRatio[2] ?? "1");
		return Number.isFinite(ratio) && ratio > 0 ? ratio : undefined;
	});

	const imageStyle = computed(() => ({
		width: "100%",
		height: "100%",
		maxHeight: effectiveMaxHeight.value
	}));

	// LSP-19030 break-it round 4: the cap shrinks the box the image is actually rendered into, so the
	// pan clamp has to know about it. Without this, useImageZoom sizes the content box from the full
	// viewport rect and the user can drag a zoomed photo past its own edge into black on any capped
	// frame taller than the cap (tablet portrait, or a tall window narrower than 960px). Same class of
	// bug as LSP-17655 review #4, reopened by restoring the cap.
	//
	// Only a px cap can be resolved to a number here; any other unit reports null, which puts the
	// clamp back on its viewport-rect behaviour rather than guessing — never leaving it stale on a
	// previous cap. Matched case-insensitively because CSS units are: `800PX` is a real 800px cap in
	// the browser, so a case-sensitive parse would leave the clamp and the rendered box disagreeing
	// (break-it round 5).
	const maxHeightPixels = computed(() => {
		const pixelMatch = effectiveMaxHeight.value.match(/^(\d+(?:\.\d+)?)px$/i);
		return pixelMatch ? Number.parseFloat(pixelMatch[1]) : null;
	});

	watch(maxHeightPixels, (pixels) => zoom.setContentMaxHeight(pixels), { immediate: true });

	let resizeObserver: ResizeObserver | null = null;
	onMounted(() => {
		zoom.setViewport(viewportEl.value);
		if (viewportEl.value && typeof ResizeObserver !== "undefined") {
			resizeObserver = new ResizeObserver(() => zoom.reclamp());
			resizeObserver.observe(viewportEl.value);
		}
	});
	onUnmounted(() => resizeObserver?.disconnect());
	watch(
		() => props.src,
		() => {
			zoom.reset();
			naturalWidth.value = 0;
			naturalHeight.value = 0;
			// The new image hasn't loaded yet — clear the previous image's content size so pan
			// limits fall back to the viewport rect until onImageLoad reports the new dimensions.
			zoom.setContentSize(0, 0);
		}
	);

	// LSP-17655 review #4: v-img renders with `object-fit: contain` inside a fixed-aspect-ratio
	// box, so a portrait/letterboxed image displays SMALLER than the viewport — pan limits must
	// clamp to the actually-rendered image box, not the outer container. The natural size from
	// this real <img> (loading the same `src`) gives useImageZoom what it needs to compute that box.
	function onImageLoad(event: Event): void {
		const img = event.target as HTMLImageElement;
		naturalWidth.value = img.naturalWidth;
		naturalHeight.value = img.naturalHeight;
		zoom.setContentSize(img.naturalWidth, img.naturalHeight);
	}

	// LSP-17655 review #1: the ancestor v-carousel's `v-touch` directive only wires up its
	// mounted/unmounted hooks (no `updated`), so it captures its swipe handlers once at mount and
	// never re-reads a reactive prop — toggling `:touch` on the carousel from here can't work.
	// Instead, stop the native touch sequence from ever bubbling to the carousel while zoomed, so
	// its swipe-to-navigate can't fire. When not zoomed, do nothing and let the swipe through.
	//
	// review #3 (HIGH): we intentionally do NOT preventDefault() here. Cancelling ANY event in a
	// touch's sequence (including touchmove) suppresses the browser's synthesized click/dblclick for
	// that touch, which would re-break double-tap-to-zoom on touch devices. Native scroll/pan is
	// already suppressed while zoomed by `touch-action: none` on the viewport, so stopPropagation
	// (carousel-swipe suppression) is all we need — no preventDefault.
	function stopTouchWhileZoomed(event: TouchEvent): void {
		if (!zoom.zoomed.value) return;
		event.stopPropagation();
	}

	// LSP-17655 review #11: a viewport resize (e.g. rotating the device, or the mobile lightbox's
	// layout reflowing) changes the pan limits without any zoom/pan action happening — re-clamp the
	// existing offset so it can't be left pointing outside the new bounds.
</script>

<style scoped>
	.zoomable-photo {
		position: relative;
		flex: 1;
		/* Fill the full height of the (row-flex) media container so the pill anchors to the bottom
		   of the black frame, not the vertical center of the letterboxed image (LSP-17655). */
		align-self: stretch;
		display: flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
	}
	.zoomable-photo__wrapper {
		width: 100%;
		height: 100%;
		/* Center the (letterboxed) image within the now full-height frame so it isn't top-anchored
		   when the container is taller than the image's rendered box (LSP-17655). */
		display: flex;
		align-items: center;
		justify-content: center;
		will-change: transform;
	}
	.zoomable-photo__pill {
		position: absolute;
		bottom: 16px;
		left: 50%;
		transform: translateX(-50%);
		display: flex;
		align-items: center;
		gap: 2px;
		background: rgb(var(--v-theme-surface));
		border-radius: 9999px;
		padding: 4px;
		box-shadow:
			0 3px 5px -1px rgba(0, 0, 0, 0.2),
			0 6px 10px 0 rgba(0, 0, 0, 0.14);
	}
	.zoomable-photo__btn {
		width: 36px;
		height: 36px;
		min-width: 36px;
		border-radius: 50%;
		color: rgb(var(--v-theme-on-surface));
	}
	/* LSP-21222: dimmed like a disabled control, but still clickable (LSP-20000) — see the template comment. */
	.zoomable-photo__btn--at-bound {
		opacity: 0.38;
		cursor: default;
	}
	.zoomable-photo__btn :deep(.v-btn__content) {
		font-size: 20px;
		line-height: 1;
	}
	/* A host's global `* { font-weight: normal }` (async base.css) reaches the label's content span; keep the 600 below. */
	.zoomable-photo__label :deep(.v-btn__content) {
		font-weight: inherit;
	}
	.zoomable-photo__label {
		min-width: 56px;
		padding: 0 8px;
		font-family: "Inter", sans-serif;
		font-size: 13px;
		font-weight: 600;
		letter-spacing: 0.01em;
		color: rgb(var(--v-theme-on-surface));
	}
</style>
